import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";

import { Feedback } from "./Feedback";
import { useShowNotification } from "../../../hooks/useShowNotificationContext";
import { AttachExtranetFilesParams } from "../../../types/atendimentos/pre-atendimento/types";
import { useCadastrarRepostaCliente, useCadastrarRetornoCliente, useFetchPesquisaSatisfacao } from "../../../services/clientes/useClientes";
import { FeedbackFilters, TipoAtendimento } from "../../../types/cliente/feedback/feedback";
import { useAnexarArquivoIntranet } from "../../../services/atendimentos/useAtendimentos";
import { NotFound } from "../../../components/NotFound";
import { LoadingOverlay } from "../../../components/Loading";
import { useFeedbackStore } from "../../../store/feedback";
import { readAsBase64 } from "../../../utils/utils";
import { useAuth } from "../../../contexts/AuthContext";
import { useFetchTicketReferenciaExterna } from "../../../services/tickets/useTickets";


export const FeedbackContainer = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { oidCliente } = useAuth()

    const { areAllSuccessfully, setStatus, resetStatuses } = useFeedbackStore();

    const converterStringParaEnum = (valor?: string): TipoAtendimento => {
        switch (valor?.trim().toUpperCase()) {
            case 'ATENDIMENTO':
                return TipoAtendimento.ATENDIMENTO;
            case 'ATENDIMENTO BASE':
                return TipoAtendimento.ATENDIMENTO_BASE;
            case 'FICHA':
                return TipoAtendimento.FICHA;
            case 'PA':
                return TipoAtendimento.PA;
            default:
                return undefined as unknown as TipoAtendimento; 
        }
    };

    const {
        data: ticketReferencia,
        error: errorTicketReferencia,
        isError: isErrorTicketReferencia,
        isLoading: isLoadingTicketReferencia,
    } = useFetchTicketReferenciaExterna(
        id,
        {
            enabled: !!id
        }
    );

    const { data: questions, refetch: refetchQuestions, isError: isErrorFetchQuestions, error: errorFetchQuestions, isLoading: isLoadingFetchQuestions } = useFetchPesquisaSatisfacao(
        converterStringParaEnum(ticketReferencia?.tipo),
        {
            enabled: !!ticketReferencia?.tipo,
        }
    )

    const defaultFilter = {
        numeroFicha: ticketReferencia?.ticket ?? 0,
        tipoAtendimento: converterStringParaEnum(ticketReferencia?.tipo),
        comentario: "",
        respostas: [],
    }

    const [attachFilesParams, setAttachFilesParams] = useState<AttachExtranetFilesParams>()
    const [files, setFiles] = useState<File[]>([]);
    const [form, setForm] = useState<FeedbackFilters>(defaultFilter)

    useEffect(() => {
        if (ticketReferencia) {
            setForm({
                numeroFicha: ticketReferencia.ticket,
                tipoAtendimento: converterStringParaEnum(ticketReferencia.tipo),
                comentario: "",
                respostas: [],
            });
        }
    }, [ticketReferencia]);

    const { showSucessNotification, showErrorNotification } = useShowNotification()
    const { mutateAsync: cadastrarRetornoCliente, isPending: isLPedingCadastro, isError: isErrorCadastro, error: errorCadastro } = useCadastrarRetornoCliente();
    const { mutateAsync: castrarRepostaCliente, isPending: isPedingResposta, isError: isErrorResposta, error: errorResposta } = useCadastrarRepostaCliente();
    const { mutateAsync: anexarArquivoIntranet, isPending: isPendingAnexo, isError: isErrorAnexo, error: errorAnexo } = useAnexarArquivoIntranet();

    const isLoadingSendForm = isLPedingCadastro || isPedingResposta || isPendingAnexo;
    const isErrorSendForm = isErrorCadastro || isErrorResposta || isErrorAnexo;
    const errorSendForm = errorCadastro || errorResposta || errorAnexo;

    const isLoading = isLoadingTicketReferencia || isLoadingFetchQuestions || isLoadingSendForm;

    const isError = errorTicketReferencia || isErrorFetchQuestions || isErrorSendForm;

    const onGoBack = () => {
        resetStatuses()
        navigate('/tickets')
    }

    const onCreate = async (formData: FeedbackFilters) => {
        await onCreateFeedback(formData)
        await onCreateCustomerFeedback(formData)

        if (attachFilesParams && Object.keys(attachFilesParams).length > 0) {
            await onSendFiles(formData.numeroFicha);
        }

        if (!areAllSuccessfully()) {
            return;
        }

        showSucessNotification("Retorno do cliente foi criado com sucesso!");
        resetStatuses()
        navigate('/tickets')
    }

    const onCreateFeedback = async (formData: FeedbackFilters) => {
        setForm(formData)

        if (formData.concluido === undefined) {
            showErrorNotification(null, 'Preencha todos os campos obrigatórios.');
            return false;
        }


        if (!oidCliente) {
            showErrorNotification(null, 'Cliente não identificado.');
            return false;
        }

        await cadastrarRetornoCliente({

            comentario: formData.comentario ?? "",
            concluido: formData.concluido,
            numFicha: formData.numeroFicha,
            oidCliente: oidCliente,
        })
            .then(() => {
                setStatus('createFeedback', true)
            })
            .catch((e) => {
                showErrorNotification(e, 'Falha ao criar o retorno do cliente: verifique os dados e tente novamente.');
            })
    }

    const onCreateCustomerFeedback = async (formData: FeedbackFilters) => {
        setForm(formData)

        if (!formData.tipoAtendimento || formData.respostas.length <= 0) {
            showErrorNotification(null, 'Preencha todos os campos obrigatórios.');
            return false;
        }

        await castrarRepostaCliente({
            numeroFicha: formData.numeroFicha,
            tipoAtendimento: formData.tipoAtendimento,
            respostas: formData.respostas,
        })
            .then(() => {
                setStatus('registerAnswer', true)
            })
            .catch((e) => {
                showErrorNotification(e, 'Falha ao criar a resposta do cliente: verifique os dados e tente novamente.');
            })
    }

    const onSendFiles = async (numeroTicket: number) => {
        let successes = 0;
        let failures = 0;

        for (const file of files) {
            const extensionFile = file.name.split(".").pop() || "";
            const baseName = file.name.replace(/\.[^/.]+$/, "");

            try {
                const base64 = await readAsBase64(file);
                await anexarArquivoIntranet({
                    numeroFicha: numeroTicket,
                    arquivo: base64,
                    nomeArquivo: baseName,
                    referencia: baseName,
                    tipoArquivo: extensionFile,
                });
                successes++;
            } catch (e) {
                console.error(e);
                failures++;
                setStatus("attachFiles", false);
                showErrorNotification(e as any, `Falha ao anexar "${file.name}".`);
            }
        }

        if (successes > 0) {
            showSucessNotification(
                successes === 1 ? "Arquivo anexado com sucesso!" : `${successes} arquivos anexados com sucesso!`
            );
        }

        if (failures === 0) {
            setAttachFilesParams({});
            setFiles([]);
        }
    };

    const onCleanFields = () => {
        setForm(defaultFilter)
        setAttachFilesParams({})
        setFiles([]);
    }

    const onRemoveFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <>

            {(isErrorSendForm || isErrorTicketReferencia) && (
                <NotFound
                    error={errorTicketReferencia ?? errorSendForm ?? errorFetchQuestions}
                    defaultMessage={isErrorTicketReferencia ? 'Erro ao carregar o ticket' : isErrorSendForm ? 'Erro ao enviar o feedback' : 'Erro ao carregar as perguntas da pesquisa de satisfação'}
                    retry={isError ? refetchQuestions : undefined}
                />
            )}


            {(isLoadingSendForm || isLoading) && <LoadingOverlay />}

            {ticketReferencia && questions && <Feedback
                files={files}
                ticket={ticketReferencia}
                form={form}
                questions={questions}
                onGoBack={onGoBack}
                onCreate={onCreate}
                onCleanFields={onCleanFields}
                onRemoveFile={onRemoveFile}
                onFilesChange={setFiles}
                onClearAllFiles={() => setFiles([])}
            />
            }
        </>
    );

}

